import { useEffect, useState, type FormEvent } from "react";
import { ArrowRight, CheckCircle2, LoaderCircle, Mail } from "lucide-react";
import { toast } from "sonner";
import { trackEvent } from "@/lib/analytics";
import { appCheckFetch } from "@/lib/firebase";

type SignupState =
  | { status: "idle"; message: "" }
  | { status: "submitting"; message: "" }
  | { status: "success"; message: string }
  | { status: "error"; message: string };

const INITIAL_STATE: SignupState = { status: "idle", message: "" };
const POPUP_SUBSCRIBED_KEY = "socrattica:podcast-newsletter:subscribed";

export function PodcastNewsletterSignup() {
  const [signupState, setSignupState] = useState<SignupState>(INITIAL_STATE);

  useEffect(() => {
    if (window.location.hash !== "#newsletter") return;
    const timers = [100, 400, 1_000, 1_900].map((delay) =>
      window.setTimeout(() => {
        document.getElementById("newsletter")?.scrollIntoView({
          behavior: delay === 100 ? "auto" : "smooth",
          block: "start",
        });
      }, delay),
    );
    return () => timers.forEach(window.clearTimeout);
  }, []);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    setSignupState({ status: "submitting", message: "" });

    try {
      const response = await appCheckFetch("/api/podcast-subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: formData.get("email"),
          consent: formData.get("consent") === "on",
          website: formData.get("website"),
        }),
      });

      const data = (await response.json().catch(() => null)) as {
        message?: string;
        signupUrl?: string;
      } | null;

      if (!response.ok) {
        const subscriptionError = new Error(
          data?.message ??
            "No pudimos procesar tu suscripción. Inténtalo nuevamente en unos minutos.",
        ) as Error & { signupUrl?: string };
        subscriptionError.signupUrl = data?.signupUrl;
        throw subscriptionError;
      }

      form.reset();
      try {
        window.localStorage.setItem(POPUP_SUBSCRIBED_KEY, "true");
      } catch {
        // La suscripción no depende de que el navegador permita usar localStorage.
      }
      const message =
        data?.message ??
        "Revisa tu correo y confirma la suscripción. Después podrás obtener tu 15% en el carrito.";
      setSignupState({
        status: "success",
        message,
      });
      toast.success(message);
      void trackEvent("podcast_subscribe", { source: "podcast_inline" });
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "No pudimos procesar tu suscripción. Inténtalo nuevamente.";
      const signupUrl = (error as Error & { signupUrl?: string })?.signupUrl;
      setSignupState({
        status: "error",
        message,
      });
      toast.error(message, signupUrl ? {
        action: {
          label: "Reinscribirme",
          onClick: () => window.open(signupUrl, "_blank", "noopener,noreferrer"),
        },
      } : undefined);
    }
  }

  const isSubmitting = signupState.status === "submitting";

  return (
    <section
      id="newsletter"
      aria-labelledby="podcast-newsletter-title"
      className="scroll-mt-28 border-t border-black/10 bg-black text-white"
    >
      <div className="mx-auto grid max-w-7xl gap-8 px-4 py-12 sm:px-6 sm:py-16 lg:grid-cols-[0.8fr_1.2fr] lg:items-center">
        <div>
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/10">
            <Mail className="h-5 w-5" />
          </span>
          <p className="mt-5 text-xs font-semibold uppercase tracking-[0.18em] text-white/55">
            Continúa aprendiendo
          </p>
          <h2
            id="podcast-newsletter-title"
            className="mt-2 text-3xl font-semibold leading-tight tracking-[-0.04em] sm:text-4xl"
          >
            Nuevos episodios y 15% de descuento.
          </h2>
          <p className="mt-3 max-w-xl text-sm leading-6 text-white/65">
            Confirma tu correo para recibir novedades y un cupón único del 15% para la librería.
          </p>
        </div>

        <div className="rounded-xl bg-white p-5 text-black sm:p-7">
          {signupState.status === "success" ? (
            <div className="py-4 text-center" role="status">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-black text-white">
                <CheckCircle2 className="h-7 w-7" />
              </span>
              <p className="mt-4 text-xl font-semibold">Suscripción lista</p>
              <p className="mx-auto mt-2 max-w-sm text-sm leading-6 text-black/60">
                {signupState.message}
              </p>
            </div>
          ) : (
            <form className="space-y-5" onSubmit={handleSubmit}>
              <div>
                <label
                  htmlFor="podcast-newsletter-email"
                  className="mb-2 block text-sm font-medium text-black"
                >
                  Correo electrónico
                </label>
                <input
                  id="podcast-newsletter-email"
                  name="email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  maxLength={254}
                  required
                  disabled={isSubmitting}
                  placeholder="tu@correo.com"
                  className="h-11 w-full rounded-lg border border-black/20 bg-white px-4 text-sm text-black outline-none transition placeholder:text-black/35 focus:border-black focus:ring-2 focus:ring-black/10 disabled:cursor-not-allowed disabled:opacity-60"
                />
              </div>

              <div className="absolute -left-[10000px] top-auto h-px w-px overflow-hidden">
                <label htmlFor="podcast-newsletter-website">
                  Sitio web
                  <input
                    id="podcast-newsletter-website"
                    name="website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </label>
              </div>

              <label className="flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-black/65">
                <input
                  name="consent"
                  type="checkbox"
                  required
                  disabled={isSubmitting}
                  className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-black"
                />
                <span>
                  Acepto recibir nuevos episodios, novedades y beneficios de Socrattica. Puedo cancelar cuando
                  quiera. Socrattica usará mi correo exclusivamente para esta suscripción.
                </span>
              </label>
              <p className="-mt-2 text-xs leading-5 text-black/55">
                Consulta cómo tratamos tu información en la{" "}
                <a
                  href="/legal/privacidad"
                  className="font-semibold text-black underline underline-offset-4"
                >
                  política de privacidad y tratamiento de datos
                </a>
                .
              </p>

              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex h-11 w-full cursor-pointer items-center justify-center gap-2 rounded-lg border border-black bg-black px-5 text-xs font-semibold text-white transition hover:bg-white hover:text-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSubmitting ? (
                  <>
                    <LoaderCircle className="h-4 w-4 animate-spin" />
                    Suscribiendo…
                  </>
                ) : (
                  <>
                    Suscribirme y obtener 15% off
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>

              <p className="text-center text-xs text-black/65">
                Sin spam. Cancela tu suscripción en cualquier momento.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
